import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Palette, Megaphone, Video, PenTool, Globe, BarChart2 } from 'lucide-react';

const MediaBrand = () => {
  const services = [
    {
      icon: Palette,
      title: 'Brand Strategy',
      description: 'Comprehensive brand development from positioning to identity. We craft brands that resonate with audiences and stand out in crowded markets.',
    },
    {
      icon: PenTool,
      title: 'Creative Direction',
      description: 'Visual storytelling that captures attention. From concept to execution, we deliver creative that moves people and drives results.',
    },
    {
      icon: Video,
      title: 'Video Production',
      description: 'High-impact video content that tells your story. From social clips to full campaigns, we produce content that performs.',
    },
    {
      icon: Megaphone,
      title: 'Campaign Development',
      description: 'Integrated marketing campaigns that deliver. We combine strategy, creative, and data to maximise impact across all channels.',
    },
    {
      icon: Globe,
      title: 'Digital Presence',
      description: 'Websites and digital experiences that convert. Beautiful, functional, and optimised for performance.',
    },
    {
      icon: BarChart2,
      title: 'Social Strategy',
      description: 'Social media that builds communities. We develop strategies that grow audiences and drive engagement.',
    },
  ];

  return (
    <div data-testid="media-brand-page">
      {/* Hero */}
      <section className="min-h-[70vh] flex items-center relative overflow-hidden">
        <div className="absolute inset-0 bg-elate-surface" />
        
        <div className="container-custom relative z-10 pt-32 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.p
                className="caption text-elate-yellow mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                Media & Brand
              </motion.p>

              <motion.h1
                className="text-headline text-white mb-8"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Brands That
                <br />
                <span className="text-elate-yellow">Perform</span>
              </motion.h1>

              <motion.p
                className="text-lg md:text-xl text-gray-400 mb-10 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                For 15+ years we've been empowering brands across sport, entertainment, music and the city. Now we combine that expertise with AI to deliver work that truly performs.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="media-cta">
                  Start a Project
                  <ArrowUpRight size={20} />
                </Link>
              </motion.div>
            </div>

            <motion.div
              className="hidden lg:block"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="relative">
                <div className="absolute -top-4 -right-4 w-full h-full border-2 border-elate-yellow" />
                <img
                  src="https://customer-assets.emergentagent.com/job_elate-web-rebuild/artifacts/jnkup3zu_lawrence%20okolie.jpg"
                  alt="Lawrence Okolie"
                  className="w-full aspect-square object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="caption text-elate-yellow mb-4">Services</p>
            <h2 className="text-subhead text-white">
              Full-Service Creative
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                className="card-elate p-8 group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <service.icon className="w-10 h-10 text-elate-yellow mb-6 group-hover:scale-110 transition-transform" />
                <h3 className="text-xl font-semibold text-white mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="section-padding bg-elate-surface">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="caption text-elate-yellow mb-4">Our Approach</p>
              <h2 className="text-subhead text-white mb-6">
                Strategy-Led Creative
              </h2>
              <p className="text-gray-400 leading-relaxed mb-6">
                Every project starts with strategy. We dig deep to understand your audience, market, and objectives before we create anything. This ensures every piece of work is purposeful and effective.
              </p>
              <p className="text-gray-400 leading-relaxed mb-8">
                We combine human creativity with AI-powered tools to deliver work faster and smarter. The result? Campaigns that look brilliant and perform even better.
              </p>
              <Link to="/work" className="btn-secondary inline-flex items-center gap-2">
                See Our Work
                <ArrowUpRight size={20} />
              </Link>
            </motion.div>

            <motion.div
              className="grid grid-cols-2 gap-4"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="p-6 border border-elate-yellow bg-elate-yellow/5">
                <p className="text-3xl font-heading text-elate-yellow mb-2">Strategy</p>
                <p className="text-gray-400 text-sm">Research & Positioning</p>
              </div>
              <div className="p-6 border border-white/20">
                <p className="text-3xl font-heading text-white mb-2">Creative</p>
                <p className="text-gray-400 text-sm">Design & Production</p>
              </div>
              <div className="p-6 border border-white/20">
                <p className="text-3xl font-heading text-white mb-2">Technology</p>
                <p className="text-gray-400 text-sm">AI & Automation</p>
              </div>
              <div className="p-6 border border-elate-yellow bg-elate-yellow/5">
                <p className="text-3xl font-heading text-elate-yellow mb-2">Results</p>
                <p className="text-gray-400 text-sm">Growth & Performance</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            className="bg-elate-yellow p-12 md:p-20 text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-subhead text-elate-black mb-6">
              Let's Build Your Brand
            </h2>
            <p className="text-elate-black/70 max-w-xl mx-auto mb-8 text-lg">
              Whether you need a complete rebrand or a single campaign, we're ready to help. Get in touch to discuss your project.
            </p>
            <Link
              to="/contact"
              className="bg-elate-black text-white font-bold uppercase tracking-wider px-8 py-4 inline-flex items-center gap-2 hover:bg-white hover:text-elate-black transition-colors"
            >
              Get in Touch
              <ArrowUpRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MediaBrand;
